import type { RouteParams } from '@/objects/RouteParams';
import { Router, type RouteName } from '@/objects/Router';
import { nameofEnum, propertyof } from '@/utilities/utils';
import { type Component } from 'vue';
import { createRouter, createWebHistory, type RouteLocationGeneric } from 'vue-router';

/**
 * Helper function for creating a redirect
 * @param name The name of the route
 * @param path The path we want to redirect from
 * @param to  Where we want to redirect to. path or routename
 * @returns
 */
const createRedirect = (name: string, path: string, to: string | ((to: RouteLocationGeneric) => string)) => {
    return {
        path: path,
        name: name,
        redirect: to,
    };
};

/**
 * Helper function for creating a route
 * @param name Name of the route
 * @param view full relative path to the view
 * @param includeEventid should the route contains the eventID parameter
 * @param roles array of strings with the roles that has access to this route
 * @returns
 */
const createRoute = (name: RouteName, view: () => Promise<Component>, includeEventid: boolean = true, roles: string[] = []) => {
    let path = `/${name}/:${propertyof<RouteParams>('tab')}?/:${propertyof<RouteParams>('id')}(\\d+|new)?/:${propertyof<RouteParams>('subtab')}?`;

    if (includeEventid) {
        path = `/:${propertyof<RouteParams>('eventID')}(\\d+)${path}`;
    }

    const routevalues = {
        path: path,
        name: name,
        component: view,
    } as any;

    if (roles.length > 0) {
        routevalues.props = { roles: roles };
    }

    return routevalues;
};

// Prevent router recreation on HMR
let router: ReturnType<typeof createRouter>;
if (import.meta.env.MODE === 'development' && typeof window !== 'undefined' && (window as any).__VITE_APP_ROUTER) {
    router = (window as any).__VITE_APP_ROUTER;
} else {
    // /(eventId) / view / tab / (child) / int | string / tab / int | string
    router = createRouter({
        history: createWebHistory(import.meta.env.BASE_URL),
        routes: [
            // Set up the redirects
            createRedirect('root', '/', '/home'),
            createRedirect('eventinfoRedirect', '/:eventID(\\d+)', (to) => `/${to.params.eventID}/dashboard`),

            // Dev demo
            ...(import.meta.env.MODE !== 'development'
                ? []
                : [createRoute('devdemo', () => import('../components/views/Dev/DevDemo.vue'), false)]),

            // before event
            createRoute('dashboard', () => import('../components/views/EventDashboard/EventDashboardView.vue')),
            createRoute('eventinfo', () => import('../components/views/EventInfo/EventInfoView.vue')),
            createRoute('contestants', () => import('../components/views/Contestants/ContestantsView.vue')),
            createRoute('categories', () => import('../components/views/Categories/CategoriesView.vue')),
            createRoute('groups', () => import('../components/views/Groups/GroupsView.vue')),
            createRoute('racesetup', () => import('../components/views/Racesetup/RacesetupView.vue')),
            createRoute('ibu', () => import('../components/views/IBU/IBUView.vue')),
            createRoute('fis', () => import('../components/views/Fis/FisView.vue')),
            createRoute('bibs', () => import('../components/views/Bibs/BibsChipsView.vue')),
            createRoute('races', () => import('../components/views/Races/RacesView.vue')),
            createRoute('quickinput', () => import('../components/views/QuickInput/QuickInputView.vue')),
            createRoute('matrix', () => import('../components/views/Matrix/MatrixView.vue')),
            createRoute('unknowntags', () => import('../components/views/UnknownTags/UnknownTagsView.vue')),
            createRoute('gpioreadings', () => import('../components/views/GPIOReadings/GPIOReadingsView.vue')),
            createRoute('eventgraphics', () => import('../components/views/EventGraphics/EventGraphicsView.vue')),
            createRoute('tracking', () => import('../components/views/Tracking/TrackingView.vue')),
            createRoute('eventdevicestatus', () => import('../components/views/DeviceStatus/EventDeviceStatusView.vue')),
            createRoute('eventfilearchive', () => import('../components/views/FileArchive/EventFileArchiveView.vue')),

            createRoute('racecontrol', () => import('../components/views/RaceControl/RaceControlView.vue')),
            createRoute('cis', () => import('../components/views/Speaker/CISView.vue')),
            createRoute('sprint', () => import('../components/views/Sprint/SprintView.vue')),
            createRoute('timerecorder', () => import('../components/views/Timerecorder/TimerecorderView.vue')),
            createRoute('reports', () => import('../components/views/Report/ReportsView.vue')),

            createRoute('traininglanes', () => import('../components/views/TrainingLanes/TrainingLanes.vue')),

            /**
             * Routes without eventID
             */
            createRoute('home', () => import('../components/views/Home/HomeView.vue'), false),
            createRoute('series', () => import('../components/views/Series/SeriesView.vue'), false),
            createRoute('filearchive', () => import('../components/views/FileArchive/FileArchiveView.vue'), false),

            createRoute('about', () => import('../components/views/AboutView.vue'), false),
            createRoute('support', () => import('../components/views/Support/SupportView.vue'), false),
            createRoute('notifications', () => import('../components/views/Notifications/NotificationsView.vue'), false),

            /**
             * Superadmin routes
             */
            createRoute('devicestatus', () => import('../components/views/DeviceStatus/DeviceStatusView.vue'), false, ['superadmin']),
            createRoute('trackdevices', () => import('../components/views/TrackDevices/TrackDevicesView.vue'), false, ['superadmin']),
            createRoute('users', () => import('../components/views/Users/UsersView.vue'), false, ['superadmin']),
            createRoute('licences', () => import('../components/views/Licences/LicencesView.vue'), false, ['superadmin']),
            createRoute(
                'approvecommunication',
                () => import('../components/views/CommunicationToApprove/CommunicationToApproveView.vue'),
                false,
                ['superadmin'],
            ),

            /**
             * This is the route for the ProdcutionView.Should this be a part of the Graphics? Im not sure. This is data that you can change during the event.
             */
            {
                path: '/:eventID(\\d+)/production/:code?',
                name: nameofEnum<RouteName>('production'),
                component: () => import('../components/views/Production/ProductionView.vue'),
            },
            {
                path: '/production/registerdevice/:code?',
                name: nameofEnum<RouteName>('productionregister'),
                props: { fullscreen: true },
                component: () => import('../components/views/Production/ProductionRegisterDeviceView.vue'),
            },

            /**
             * 404 not found
             */
            {
                path: '/notfound',
                name: nameofEnum<RouteName>('notfound'),
                component: () => import('../components/views/Status/NotFoundView.vue'),
            },
        ],
    });

    /**
     * This is a global navigation guard that will be called before each route change.
     */
    router.beforeEach(
        /**
         * Function that will be called before each route change.
         * @param to the route we are going to
         * @param from the route we are coming from
         * @returns
         */
        (to, from) => {
            if (to.path.endsWith('/')) {
                return to.fullPath.replace(to.path, to.path.slice(0, -1));
            }

            // If there is no query we dont care.
            if (Object.keys(from.query).length + Object.keys(to.query).length === 0) {
                return true;
            }

            // Create a Url object without any query parameters. Just to get a base url.
            const url = new URL(document.location.protocol + '//' + document.location.host + to.path);

            // Whitelist of query parameters that are allowed to be passed to the next route.
            // fullscreen = remove all the menues and framing of the layout
            // theme = forces a theme and ignores user preferences values are 'light' or 'dark'
            // production = bigscreen to use in events
            // ua = useragent as base64
            const whitelist = ['fullscreen', 'theme', 'production', 'ua', 'code'];

            // We need to copy the query parameters from the from route to the to route.
            const route = Object.assign({}, from.query, to.query);
            to.query = {};
            // Loop through the query parameters and add them to the url and to the to.query object.
            for (const key in route) {
                if (whitelist.includes(key.toLowerCase())) {
                    url.searchParams.append(key, route[key]?.toString() ?? '');
                    to.query[key] = route[key];
                }
            }

            // Create a new url with the kept query parameters and remove the base url from the fullpath.
            to.fullPath = url.toString().replace(url.protocol + '//' + url.host, '');

            return true;
        },
    );

    router.beforeResolve((to) => {
        try {
            Router.UpdateRoute(to);
        } catch (e) {
            if ((e as Error).cause !== 'IdenticalRouteException') {
                console.error('Route not found', e);
                Router.NavigateTo({}, 'notfound');
                return;
            } else {
                console.log('Route is the same as current route', e);
            }
        }
        return true;
    });

    if (typeof window !== 'undefined') {
        (window as any).__VITE_APP_ROUTER = router;
    }
}

export default router;
